import React, {useState} from 'react';
import {Box, Typography, CircularProgress} from '@mui/material';
import {NAVY, PageHeader, BandChip, UseWithCareBanner, riskBandColor, RISK_BAND_COLORS} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, riskList, titleCase} from '../networkData';
import {isSuppressedItem} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';

/* Slide 23 — Cyber Risk by Gender (SND-23-01, SND-23-02).

     SND-23-01  Select a risk           SND.S_8.C_2   ListCard (risks by family)
     SND-23-02  selected risk by gender SND.S_8.C_3   BarChartBMCard

   The chart card holds every risk × gender pair; each item names its risk by
   code and its gender in dims.gender. Picking a risk on the left shows that
   risk's groups, exactly as the datahouse sends them. A group of fewer than
   10 students is still sent, flagged `suppressed` — its figure is withheld
   rather than drawn, as the School dashboard's own tabs do. */

const RISK_BAND_LEGEND = Object.keys(RISK_BAND_COLORS);

const CYBER_RISK_BY_GENDER_SECTION_CODE = 'S_8';
const CARD_SECTIONS = [CYBER_RISK_BY_GENDER_SECTION_CODE];

const CyberRiskByGenderTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });
    const [picked, setSelected] = useState(null);

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const RISKS = riskList(findCard(dashboardData, CYBER_RISK_BY_GENDER_SECTION_CODE, 'C_2'));
    const current = RISKS.find((r) => r.code === picked) || RISKS[0] || null;
    const selected = current?.name || '';

    const GENDER_ROWS = (findCard(dashboardData, CYBER_RISK_BY_GENDER_SECTION_CODE, 'C_3')?.items || [])
        .filter((item) => current && item.code === current.code)
        .map((item) => ({
            key: String(item.dims?.gender ?? item.label),
            label: titleCase(item.dims?.gender || ''),
            // A group under 10 students arrives flagged rather than omitted.
            pct: isSuppressedItem(item) ? null : whole(item.value),
            band: isSuppressedItem(item) ? null : item.band,
            suppressed: isSuppressedItem(item),
        }));

    return (
        <Box>
            <PageHeader
                title="Cyber Risk by Gender"
                subtitle="Do reported risks differ across gender groups, so support can be inclusive?"
            />

            <UseWithCareBanner text="Risk prevalence is a status, not a competency score — higher means more students affected. Respond to the whole cohort rather than targeting any one group, and keep all communications supportive and inclusive."/>

            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'flex-start', flexDirection: {xs: 'column', md: 'row'}}}>
                <Box sx={{
                    width: {xs: '100%', md: '320px'}, flexShrink: 0,
                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 2.5,
                }}>
                    <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 1.5}}>Select a risk</Typography>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>
                        {RISKS.map((risk) => {
                            const active = current?.code === risk.code;
                            return (
                                <Box
                                    key={risk.code}
                                    onClick={() => setSelected(risk.code)}
                                    sx={{
                                        cursor: 'pointer', borderRadius: '8px', px: 1.25, py: 0.75,
                                        backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'transparent',
                                        '&:hover': {backgroundColor: active ? 'rgba(255, 105, 0, 0.08)' : 'rgba(0, 0, 0, 0.03)'},
                                    }}
                                >
                                    <Typography sx={{fontSize: '13px', color: active ? NAVY : '#374151', fontWeight: active ? 700 : 400}}>
                                        {risk.num}&nbsp;&nbsp;{risk.name}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <Box sx={{
                    flex: 1, minWidth: 0, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                    backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3,
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, flexWrap: 'wrap', gap: 1}}>
                        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{selected}</Typography>
                        <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
                            {RISK_BAND_LEGEND.map((l) => <BandChip key={l} label={l} color={riskBandColor(l)}/>)}
                        </Box>
                    </Box>
                    <Typography sx={{fontSize: '13px', color: '#6A7282', mb: 3}}>
                        Percentage of students who experienced this risk in the past year
                    </Typography>

                    {GENDER_ROWS.map((row) => (row.suppressed ? (
                        <Box key={row.key} sx={{mb: 2}}><SuppressedValue label={row.label}/></Box>
                    ) : (
                        <Box key={row.key} sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2}}>
                            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 60, flexShrink: 0}}>{row.label}</Typography>
                            <Box sx={{flex: 1, position: 'relative', height: 16, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '8px'}}>
                                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${row.pct || 0}%`, backgroundColor: riskBandColor(row.band), borderRadius: '8px', minWidth: row.pct === null ? 0 : '4px'}}/>
                            </Box>
                            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, width: 44, textAlign: 'right', flexShrink: 0}}>{row.pct === null ? '—' : `${row.pct}%`}</Typography>
                        </Box>
                    )))}
                </Box>
            </Box>
        </Box>
    );
};

export default CyberRiskByGenderTab;
